import React from 'react';
import { Formik, yupToFormErrors } from 'formik';
import { apiPost } from 'helpers/ApiHelper';
import Auth from 'j-toker';
import TextBox from 'components/ui/TextBox';
import DatePicker from 'components/ui/DatePicker';
import Select from 'components/ui/SelectField';
import Form from 'components/ui/Form';
import Row from 'components/ui/Row';
import CheckboxNormal from 'components/ui/CheckboxNormal';
import Col from 'components/ui/Col';
import styled from 'styled-components';
import Spacer from 'components/ui/Spacer';
import { toFriendlyDateString } from 'helpers/Formatter';
import * as Yup from 'yup';
import moment from 'moment';
import bedImage from 'assets/bed.svg';
import { getTranslatedString, getCurrentLocale } from 'helpers/i18n';
import ReactPhoneInput from 'components/shared/ReactPhoneInput';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { isValidEmail } from '../../helpers/Validator';
import { VerticalFormWrapper } from 'components/ui/VerticalFormWrapper';

const StyledLinkBack = styled.span`
  color: var(--ultramarine-blue);
  cursor: pointer;
`;

const StyledColumn = styled(Col)`
  margin: 8px 0;
`;

const SummaryBox = styled.div`
  border-radius: 16px;
  box-shadow: 0 2px 6px 0 rgba(0, 17, 80, 0.21);
  padding: 16px 24px;
  background: #fff;
`;

const Split = styled.div`
  display: flex;
  justify-content: space-between;
`;

const FullWidthSection = styled.div`
  width: 100%;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  width: 10rem;
  align-items: flex-end;
`;

const Place = styled.div`
  color: #65666d;
`;
const Time = styled.div`
  color: #2b2c32;
  font-family: 'Circular-Bold';
`;

const FormTitle = styled.span`
  font-size: 20px;
  font-family: 'Circular-Bold';
`;

const FullWidthInput = styled(TextBox)`
  width: 100%;
`;
const FullWidthSelect = styled(Select)`
  width: 100%;
`;

const FullWidthDatePicker = styled(DatePicker)`
  width: 100%;
`;

class CreateShowingDetailsStepOnlineBooking extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      searchResult: [],
      selectedItem: undefined,
    };
  }

  handleFormSubmit = (values) => {
    const { selectedItem } = this.state;
    const selectedItemFullName = selectedItem && selectedItem.data && selectedItem.data.full_name;
    const payload = {
      ...values,
      property_id: this.props.property.id,
      full_name: selectedItemFullName || `${values.first_name} ${values.last_name}`,
      book_date: values.book_date && toFriendlyDateString(values.book_date, { language: 'en' }),
      move_in_date:
        values.move_in_date && toFriendlyDateString(values.move_in_date, { language: 'en' }),
    };
    this.props.onLoadingChange({ loading: true, error: false });
    apiPost(Auth.getApiUrl() + '/landlords/create_lead_booking', payload)
      .done((response) => {
        this.props.onSubmissionComplete(payload);
      })
      .catch((error) => {
        this.props.onLoadingChange({
          formData: payload,
          loading: false,
          error: true,
        });
      });
  };

  validateForm = async (values) => {
    const requiredErrorVerbiage = getTranslatedString('required');
    const invalidEmailVerbiage = getTranslatedString('invalidEmail');
    const invalidPhoneVerbiage = getTranslatedString('validationErrors.invalidPhone');

    let validationSchema = {
      max_budget: Yup.string().required(requiredErrorVerbiage),
      move_in_date: Yup.date().required(requiredErrorVerbiage),
      traffic_source: Yup.string().required(requiredErrorVerbiage),
      first_name: Yup.string().required(requiredErrorVerbiage),
      last_name: Yup.string().required(requiredErrorVerbiage),
      email: Yup.string()
        .required(requiredErrorVerbiage)
        .test('email', invalidEmailVerbiage, function (value) {
          if (!value) return false;
          return isValidEmail(value);
        }),
      phone_number: Yup.string()
        .required(requiredErrorVerbiage)
        .test('phone_number', invalidPhoneVerbiage, function (value) {
          if (value === '+00000000000') return true;
          if (!value) return false;
          const parseResult = parsePhoneNumberFromString(value);
          return parseResult && parseResult.isValid();
        }),
      budget_range: Yup.string().required(requiredErrorVerbiage),
    };

    if (this.props.property.virtual_tour) {
      validationSchema['virtual_tour'] = Yup.string().required(requiredErrorVerbiage);
    }

    if (values.virtual_tour === 'Other') {
      validationSchema['virtual_tour_other'] = Yup.string().required(requiredErrorVerbiage);
    }

    const schema = Yup.object().shape(validationSchema);
    return schema.validate(values, { abortEarly: false }).catch((error) => {
      throw yupToFormErrors(error);
    });
  };

  render() {
    const { slotSearchData } = this.props;
    return (
      <React.Fragment>
        <Spacer mb={2}>{this.renderShowingSummary()}</Spacer>
        <Formik
          initialValues={{
            property_id: slotSearchData.property.value,
            desired_unit: slotSearchData.unitType.map((unitType) => unitType.value),
            book_date: slotSearchData.bookDate,
            book_time: slotSearchData.timeSlot[0],
            lead_id: undefined,
            person: undefined,
            first_name: undefined,
            last_name: undefined,
            email: undefined,
            phone_number: undefined,
            move_in_date: undefined,
            traffic_source: undefined,
            notes: undefined,
            max_budget: undefined,
            min_budget: undefined,
            budget_range: undefined,
            virtual_tour: undefined,
            virtual_tour_other: undefined,
            notify_by_sms: true,
            notify_by_email: true,
          }}
          onSubmit={this.handleFormSubmit}
          validate={this.validateForm}
        >
          {({ values, errors, touched, handleChange, setFieldValue, handleSubmit }) => {
            return (
              <form id={this.props.formName} onSubmit={handleSubmit}>
                {this.renderCreateLead(values, errors, touched, handleChange, setFieldValue)}
                {this.renderAdditionalInformation(
                  values,
                  errors,
                  touched,
                  handleChange,
                  setFieldValue
                )}
              </form>
            );
          }}
        </Formik>
      </React.Fragment>
    );
  }

  renderShowingSummary = () => {
    const { slotSearchData, handleBack, setRef } = this.props;
    return (
      <SummaryBox ref={setRef}>
        <Split>
          <FullWidthSection>
            <Spacer mt={0.6}>
              <Place>{slotSearchData.property.label || slotSearchData.property.name}</Place>
              <Time>
                {toFriendlyDateString(slotSearchData.bookDate)}, {slotSearchData.timeSlot[0]} -{' '}
                {slotSearchData.timeSlot[1]}
              </Time>
              <StyledLinkBack onClick={handleBack}>back</StyledLinkBack>
            </Spacer>
          </FullWidthSection>
          <Section>
            <img src={bedImage} alt="Bedroom Icon" />
            {!slotSearchData.property.hide_unit_types && (
              <div>
                {slotSearchData.unitType
                  .map((unitType) => {
                    if (unitType.translation_key) {
                      return getTranslatedString(`standardUnitTypes.${unitType.translation_key}`);
                    } else {
                      return unitType.label;
                    }
                  })
                  .join(', ')}
              </div>
            )}
          </Section>
        </Split>
      </SummaryBox>
    );
  };

  renderCreateLead = (values, errors, touched, handleChange, setFieldValue) => {
    return (
      <React.Fragment>
        <VerticalFormWrapper>
          <Spacer mb={1}>
            <FormTitle>{getTranslatedString('showingDrawer.allWeNeedIsContactDetails')}</FormTitle>
          </Spacer>
          <Row gutter={16}>
            <StyledColumn sm={12} xs={24}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.firstName')}*`}
                validateStatus={touched.first_name && errors.first_name && 'error'}
                help={touched.first_name && errors.first_name}
              >
                <FullWidthInput
                  name="first_name"
                  value={values.first_name}
                  onChange={handleChange}
                  placeholder="E.g. James"
                  size="large"
                />
              </Form.Item>
            </StyledColumn>
            <StyledColumn sm={12} xs={24}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.lastName')}*`}
                validateStatus={touched.last_name && errors.last_name && 'error'}
                help={touched.last_name && errors.last_name}
              >
                <FullWidthInput
                  name="last_name"
                  value={values.last_name}
                  onChange={handleChange}
                  placeholder="E.g. Ford"
                  size="large"
                />
              </Form.Item>
            </StyledColumn>
          </Row>
          <Row gutter={16}>
            <StyledColumn sm={12} xs={24}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.email')}*`}
                validateStatus={touched.email && errors.email && 'error'}
                help={touched.email && errors.email}
              >
                <FullWidthInput
                  name="email"
                  value={values.email}
                  onChange={handleChange}
                  placeholder="E.g. james@ford.com"
                  size="large"
                />
              </Form.Item>
            </StyledColumn>
            <StyledColumn sm={12} xs={24}>
              <Form.Item
                label={`${getTranslatedString('showingDrawer.phoneNumber')}*`}
                validateStatus={touched.phone_number && errors.phone_number && 'error'}
                help={touched.phone_number && errors.phone_number}
              >
                <ReactPhoneInput
                  value={values.phone_number}
                  onChange={(value) => setFieldValue('phone_number', value)}
                  hasError={touched.phone_number && errors.phone_number}
                />
              </Form.Item>
            </StyledColumn>
          </Row>
        </VerticalFormWrapper>
      </React.Fragment>
    );
  };

  renderAdditionalInformation = (values, errors, touched, handleChange, setFieldValue) => {
    const budgetRanges = [
      ...this.props.property.rent_ranges,
      getTranslatedString('landlordPortal.flexibleBudget'),
    ];

    return (
      <React.Fragment>
        <VerticalFormWrapper>
          <Spacer mb={1}>
            <Row gutter={16}>
              <StyledColumn sm={12} xs={24}>
                <Form.Item
                  label={`${getTranslatedString('showingDrawer.moveInDate')}*`}
                  validateStatus={touched.move_in_date && errors.move_in_date && 'error'}
                  help={touched.move_in_date && errors.move_in_date}
                >
                  <FullWidthDatePicker
                    name="move_in_date"
                    defaultPickerValue={moment().locale(getCurrentLocale())}
                    showToday={false}
                    value={values.move_in_date}
                    onChange={(value) => setFieldValue('move_in_date', value, true)}
                    disabledDate={(current) =>
                      current && current < moment().subtract(1, 'day').endOf('day')
                    }
                    minDate={new Date()}
                    mobileMode="date"
                    placeholder="E.g. 2020-01-01"
                    size="large"
                    id="error"
                  />
                </Form.Item>
              </StyledColumn>
              <StyledColumn sm={12} xs={24}>
                <Form.Item
                  label={`${getTranslatedString('showingDrawer.howDidYouHear')}*`}
                  validateStatus={touched.traffic_source && errors.traffic_source && 'error'}
                  help={touched.traffic_source && errors.traffic_source}
                >
                  <FullWidthSelect
                    name="traffic_source"
                    value={values.traffic_source}
                    onChange={(value) => setFieldValue('traffic_source', value, true)}
                    placeholder={getTranslatedString('showingDrawer.select')}
                    size="large"
                  >
                    {this.props.trafficSources.map((source) => (
                      <Select.Option value={source.id}>{source.traffic_source}</Select.Option>
                    ))}
                  </FullWidthSelect>
                </Form.Item>
              </StyledColumn>
            </Row>
            <Row gutter={16}>
              <StyledColumn sm={24} xs={24}>
                <Form.Item
                  label={`${getTranslatedString('landlordPortal.budgetRange')}*`}
                  validateStatus={touched.budget_range && errors.budget_range ? 'error' : null}
                  help={touched.budget_range && errors.budget_range}
                >
                  <FullWidthSelect
                    name="budget_range"
                    value={values.budget_range}
                    onChange={(value) => {
                      const budgetValues = value.split(' - ');
                      if (budgetValues.length === 2) {
                        setFieldValue('min_budget', budgetValues[0]);
                        setFieldValue('max_budget', budgetValues[1]);
                      } else if (budgetValues.length === 1 && budgetValues[0].includes('$')) {
                        setFieldValue('min_budget', budgetValues[0]);
                        setFieldValue('max_budget', budgetValues[0]);
                      } else {
                        setFieldValue('min_budget', '$0.00');
                        setFieldValue('max_budget', '$0.00');
                      }
                      setFieldValue('budget_range', value, true);
                    }}
                    placeholder={getTranslatedString('showingDrawer.select')}
                    size="large"
                  >
                    {budgetRanges.map((option) => (
                      <Select.Option value={option}>{option}</Select.Option>
                    ))}
                  </FullWidthSelect>
                </Form.Item>
              </StyledColumn>
            </Row>
            {this._renderPreferredVirtualCommunication(
              values,
              errors,
              touched,
              handleChange,
              setFieldValue
            )}
            <Row gutter={16}>
              <StyledColumn sm={24} xs={24}>
                <Form.Item
                  label={`${getTranslatedString(
                    'landlordPortal.showingDetails.howWouldYouPrefer'
                  )}*`}
                >
                  <Section style={{ alignItems: 'flex-start', width: '100%' }}>
                    <CheckboxNormal
                      name="notify_by_sms"
                      value={values.notify_by_sms}
                      checked={values.notify_by_sms}
                      onChange={handleChange}
                    >
                      {getTranslatedString('landlordPortal.showingDetails.sms')}
                    </CheckboxNormal>
                    <CheckboxNormal
                      name="notify_by_email"
                      value={values.notify_by_email}
                      checked={values.notify_by_email}
                      onChange={handleChange}
                    >
                      {getTranslatedString('landlordPortal.showingDetails.email')}
                    </CheckboxNormal>
                  </Section>
                </Form.Item>
              </StyledColumn>
            </Row>
          </Spacer>
        </VerticalFormWrapper>
      </React.Fragment>
    );
  };

  _renderPreferredVirtualCommunication = (values, errors, touched, handleChange, setFieldValue) => {
    if (!this.props.property.virtual_tour) return;

    return (
      <React.Fragment>
        <Row gutter={16}>
          <StyledColumn sm={24} xs={24}>
            <Form.Item
              label={`${getTranslatedString(
                'landlordPortal.showingDetails.howWouldYouPreferVirtualTour'
              )}*`}
              validateStatus={touched.virtual_tour && errors.virtual_tour ? 'error' : null}
              help={touched.virtual_tour && errors.virtual_tour}
            >
              <Section style={{ alignItems: 'flex-start', width: '100%' }}>
                <FullWidthSelect
                  name="virtual_tour"
                  value={values.virtual_tour}
                  onChange={(value) => setFieldValue('virtual_tour', value, true)}
                  placeholder={getTranslatedString('showingDrawer.select')}
                  size="large"
                >
                  <Select.Option value="FaceTime">FaceTime</Select.Option>
                  <Select.Option value="WhatsApp">WhatsApp</Select.Option>
                  <Select.Option value="Skype">Skype</Select.Option>
                  <Select.Option value="Google Duo">Google Duo</Select.Option>
                  <Select.Option value="Other">
                    {getTranslatedString('landlordPortal.showingDetails.otherPleaseSpecify')}
                  </Select.Option>
                </FullWidthSelect>
              </Section>
            </Form.Item>
          </StyledColumn>
        </Row>
        {values.virtual_tour === 'Other' && (
          <Row gutter={16}>
            <StyledColumn sm={24} xs={24}>
              <Form.Item
                label={`${getTranslatedString(
                  'landlordPortal.showingDetails.virtualTourPleaseSpecify'
                )}*`}
                validateStatus={touched.virtual_tour_other && errors.virtual_tour_other && 'error'}
                help={touched.virtual_tour_other && errors.virtual_tour_other}
              >
                <FullWidthInput
                  name="virtual_tour_other"
                  value={values.virtual_tour_other}
                  onChange={handleChange}
                  size="large"
                />
              </Form.Item>
            </StyledColumn>
          </Row>
        )}
      </React.Fragment>
    );
  };
}

export default CreateShowingDetailsStepOnlineBooking;
